import React, { ReactElement } from 'react';

import { createInstance, createStandardApi, PiletApi, PiletEntry, Piral, PiralPlugin } from 'piral';
import errors from './layouts/errors';
import APIManager from './data-layer/APIManager';
import ConnectivityManager from './data-layer/ConnectivityManager';
import ClientDispatcher, { CLIENT_DISPATCHER_ID } from './data-layer/dispatchers/ClientDispatcher';
import ServerDispatcher, { SERVER_DISPATCHER_ID } from './data-layer/dispatchers/ServerDispatcher';
import ConnectivityHolder, { ConnectivityInfo } from './data-layer/ConnectivityHolder';
import ExtensionComponent from './ExtensionComponent';
import pageLayout from './layouts/pageLayout';
import PageFactory from './utils/PageFactory';
import PiletLoadTracker from './utils/PiletLoadTracker';
import createDefaultEvents from './utils/createDefaultEvents';
import DefaultEvents from './utils/DefaultEvents';
import { EventEmitter } from 'events';
import { EnrichedComponent } from '@/backend/getComponents';
import ErrorMessage from './components/ErrorMessage';

type PageType = {
  layout: {
    id?: string,
    instanceOf: string,
    components: {
      [region: string]: EnrichedComponent
    }
  },
  route: string,
  components: EnrichedComponent[]
}
type PagesType = {
  [pageID: string]: PageType
}

type BackendData = {
  pages: {
    [pageID: string]: PageType
  },
  connectivity: ConnectivityInfo
}
type ErrorData = {
  error: string
}
type PiralInstanceType = {
  data: BackendData | ErrorData,
}

export default function PiralInstance({ data }: PiralInstanceType): ReactElement {
  // Check if data contains an error
  if ('error' in data) {
    return <ErrorMessage message={data.error} />;
  }

  const url = new URL(document.location.href);
  const urlParams = url.searchParams;
  const configID = urlParams.get('config');
  const eventMgr = new EventEmitter();

  const page = url.pathname.replace('/', '');

  console.info('*** page: ' + page);

  const jsxLayout = pageLayout;

  const piralAPI = createStandardApi();

  const instance = createInstance({
    state: {
      components: jsxLayout,
      errorComponents: errors,
    },
    plugins: [piralAPI as unknown as PiralPlugin<Partial<PiletApi>>],
    requestPilets: requestedPilets(data, eventMgr)
  });

  // eslint-disable-next-line sonarjs/constructor-for-side-effects
  new PiletLoadTracker({
    eventMgr: eventMgr,
    initialPage: instance.context.navigation.path
  });

  instance.root.setData('configid', configID);
  instance.root.setData('page', page);
  createDefaultEvents(eventMgr, instance);
  instance.root.setData('eventMgr', eventMgr);
  const apiMgr = new APIManager();
  instance.root.setData('apiMgr', apiMgr);

  instance.root.setData('reactWrapperComp', ExtensionComponent);

  instance.root.setData('fireEvent', function (componentID: string, topic: string, params?: object) {
    eventMgr.emit(componentID + '-' + topic, params)
  });

  console.log('connectivity should be contained in: ', data);
  setupConnectivityManagers(data, eventMgr, apiMgr);

  registerPages(instance.root, data.pages, eventMgr);
  return <Piral instance={instance} />
}

const registerPages = (app: PiletApi, pages: PagesType, eventMgr: EventEmitter) => {
  for (const [id, value] of Object.entries(pages)) {
    const { route, layout } = value;
    const { components, id: layoutType, instanceOf: layoutInstanceOf } = layout;
    const routeKey = route.replace('/', '');
    app.registerPage(route, PageFactory.createPage({ pageKey: routeKey, layoutType: layoutInstanceOf || layoutType, components: Object.values(components) }));
    for (const region in components) {
      const { config = {}, instanceID, id: componentID, instanceOf, setupCode } = components[region];
      const slotKey = `${routeKey}-${region}`;

      eventMgr.emit(DefaultEvents.ON_PILET_ADDED_TO_PAGE, {
        id: componentID,
        page: route
      });

      //FBA: check why this was introduced again, it doesnt really work
      app.setData(id, slotKey);
      app.setData(`region_${instanceID || componentID}`, slotKey);
      app.setData(`props_${instanceID || componentID}`, config);
      app.setData(`${instanceID}-setupCode`, setupCode);
      app.setData(`${instanceID}-setupData`, {
        uniqueID: instanceID,
        componentID: instanceOf || componentID,
        configID: app.getData('configid'),
        page: routeKey
      })
    }
  }
}



function setupConnectivityManagers(data: BackendData, eventMgr: EventEmitter, apiMgr: APIManager) {
  const connectivityHolder = new ConnectivityHolder(data.connectivity);
  const connectivityManager = new ConnectivityManager({ holder: connectivityHolder, eventManager: eventMgr });

  connectivityManager.registerDispatcher(CLIENT_DISPATCHER_ID, new ClientDispatcher(connectivityHolder, eventMgr, apiMgr, connectivityManager));
  connectivityManager.registerDispatcher(SERVER_DISPATCHER_ID, new ServerDispatcher(connectivityHolder));
}

function requestedPilets({ pages }: { pages: { [key: string]: PageType } }, eventMgr: EventEmitter) {
  return async function () {
    const urlsWithComponents: PiletEntry[] = [];
    for (const page of Object.values(pages)) {
      const { layout } = page;
      const { components } = layout;
      for (const region in components) {
        const component = components[region]
        urlsWithComponents.push(
          getPiletMetaData(component)
        );
      }
      if (page.components) {
        page.components.forEach(component => {
          urlsWithComponents.push(
            getPiletMetaData(component)
          );
        });
      }
    }
    eventMgr.emit(DefaultEvents.ON_CONFIG_LOADED);
    return urlsWithComponents;
  }
}
function getPiletMetaData(component: EnrichedComponent): PiletEntry {
  return {
    link: component.url,
    name: component.instanceID,
    version: '1.0.0',
    spec: 'v2',
    custom: {
      id: component.instanceOf || component.id,
      instanceID: component.instanceID
    }
  };
}

